import React, {useEffect, useState, useMemo} from "react"
import { ScriptHelper } from '@invicara/ipa-core/esm_modules/IpaUtils'
import { copyToClipboard } from "../utils/dev-mgmt-utils"

import './CreateSandboxView.scss'

const CreateSandboxView = ({handler}) => {
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [appOwner, setAppOwner] = useState('')

  const [isReady, setIsReady] = useState(false)
  const [creatingApp, setCreatingApp] = useState(false)

  const [request, setRequest] = useState('')
  const [response, setResponse] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Get environment info
  const envInfo = useMemo(() => {
    const env = handler?.config?.env || 'Unknown'
    const host = handler?.config?.host || ''
    return { env, host }
  }, [handler])

  useEffect(() => {
    setShortName(processShortName(name))
  }, [name])

  useEffect(() => {
    setIsReady(!!name.trim().length && !!shortName.trim().length && shortName.length <= 22 && !!appOwner.trim().length && emailIsValid(appOwner))
  }, [name, shortName, appOwner])

  const processShortName = (sname) => {
    return sname.slice(0, 22).replaceAll(' ', '').toLowerCase().replace(/[^a-z0-9_]/g, '_')
  }

  const emailIsValid = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email)
  }

  const clearForm = () => {
    setId('')
    setName('')
    setShortName('')
    setAppOwner('')
    setRequest('')
    setResponse('')
    setError('')
    setSuccessMessage('')
  }

  const createApp = async () => {
    setCreatingApp(true)
    setError('')
    setSuccessMessage('')

    try {
      let t_id = id.trim()
      let t_name = name.trim()
      let t_shortName = shortName.trim()
      let t_appOwner = appOwner.trim()

      let result = await ScriptHelper.executeScript(handler.config.scripts.createSandboxApp, {
        id: t_id.length ? t_id : null,
        name: t_name,
        shortName: t_shortName,
        appOwner: t_appOwner
      })

      setRequest(JSON.stringify(result.createRequest, null, 2))
      setResponse(JSON.stringify(result.createResult, null, 2))
      setSuccessMessage('Application created successfully!')
    } catch (err) {
      console.error('Failed to create application:', err)
      setError(err.message || 'Failed to create application')
    } finally {
      setCreatingApp(false)
    }
  }

  return (
    <div className="create-sandbox-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <i className="fas fa-cube"></i>
          <h1>Create Application</h1>
        </div>
        <p className="header-subtitle">
          Create a new application in the {envInfo.env} environment
        </p>
        {envInfo.host && (
          <div className="env-badge">
            <i className="fas fa-server"></i>
            <span>{envInfo.host}</span>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="message-banner error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="message-banner success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      <div className="create-sandbox-content">
        {/* Form Section */}
        <div className="section-card form-section">
          <h2>
            <i className="fas fa-edit"></i>
            Application Details
          </h2>
          <p className="section-description">
            Fill in the details for the new application. The short name will be auto-generated from the name.
          </p>

          <div className="form-container">
            <div className="form-group">
              <label>
                Application ID
                <span className="form-hint-inline"> (optional)</span>
              </label>
              <input
                type="text"
                value={id}
                placeholder="Leave empty for auto-generated ID"
                disabled={creatingApp}
                onChange={(e) => setId(e.target.value)}
              />
              <span className="form-hint">If provided, the application will use this specific ID</span>
            </div>

            <div className="form-group">
              <label>
                Application Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={name}
                placeholder="My Application"
                disabled={creatingApp}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>
                Application Short Name <span className="required">*</span>
                <span className="form-hint-inline"> (max 22 chars, auto-generated)</span>
              </label>
              <input
                type="text"
                value={shortName}
                placeholder="my_application"
                disabled={creatingApp}
                maxLength={22}
                onChange={(e) => setShortName(processShortName(e.target.value))}
              />
              {shortName && shortName.length > 22 && (
                <span className="form-error">Must be 22 characters or less</span>
              )}
              <span className="form-hint">{shortName.length}/22 characters</span>
            </div>

            <div className="form-group">
              <label>
                Application Owner Email <span className="required">*</span>
              </label>
              <input
                type="email"
                value={appOwner}
                placeholder="owner@example.com"
                disabled={creatingApp}
                onChange={(e) => setAppOwner(e.target.value)}
              />
              {appOwner && !emailIsValid(appOwner) && (
                <span className="form-error">Please enter a valid email address</span>
              )}
              <span className="form-hint">This user will be the owner of the application</span>
            </div>

            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={clearForm}
                disabled={creatingApp}
              >
                <i className="fas fa-eraser"></i>
                Clear All
              </button>
              <button
                className="btn-primary"
                onClick={createApp}
                disabled={!isReady || creatingApp}
              >
                {creatingApp ? (
                  <><i className="fas fa-spinner fa-spin"></i> Creating...</>
                ) : (
                  <><i className="fas fa-plus"></i> Create Application</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="section-card results-section">
          <h2>
            <i className="fas fa-terminal"></i>
            Creation Results
          </h2>

          {creatingApp && (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Creating Application...</span>
            </div>
          )}

          {!creatingApp && !request && !response && (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No results yet</p>
              <p className="hint">Create an application to see the results here</p>
            </div>
          )}

          {request && (
            <div className="warning-banner">
              <i className="fas fa-exclamation-triangle"></i>
              <div>
                <strong>Important:</strong> Copy the request and response information before leaving this page.
                This data is needed for the application owner and your records.
              </div>
            </div>
          )}

          {request && (
            <div className="result-block">
              <div className="result-header">
                <span>Request</span>
                <button className="btn-copy" onClick={() => copyToClipboard(request, 'request')}>
                  <i className="fas fa-copy"></i>
                  Copy
                </button>
              </div>
              <pre>{request}</pre>
            </div>
          )}

          {response && (
            <div className="result-block">
              <div className="result-header">
                <span>Response</span>
                <button className="btn-copy" onClick={() => copyToClipboard(response, 'response')}>
                  <i className="fas fa-copy"></i>
                  Copy
                </button>
              </div>
              <pre>{response}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CreateSandboxView
